// Fuzzy matching of spreadsheet column headers onto API model fields.
//
// Lifted out of ChatAssistant.tsx and generalised to take an arbitrary field list, so
// every import flow (and every target model) shares one matcher instead of each
// growing its own alias table. MultiTabTradeFileParser had a strictly weaker copy of
// this logic - prefer these functions over re-implementing.

/** Minimum score at which an automatic match is considered trustworthy. */
export const MIN_COLUMN_MATCH_SCORE = 0.8;

export interface ColumnMatchable {
    /** Stable field key, e.g. 'tradeDate'. */
    key: string;
    /** Header spellings that should resolve to this field. */
    aliases: string[];
}

export interface ColumnMatch {
    fieldKey: string;
    /** [0, 1]; 1.0 is an exact normalized match. */
    score: number;
}

/**
 * Normalize a column name so casing, separators, camelCase boundaries and the
 * "required" asterisk all collapse: "Trade_Date", "TRADE-DATE *" and "tradeDate" all
 * become "trade date".
 */
export function normalizeColumnName(name: string): string {
    return String(name ?? '')
        // Split camelCase / PascalCase boundaries first so "tradeDate" becomes
        // "trade Date" and "POBox" becomes "PO Box".
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
        .toLowerCase()
        .replace(/[*\u00d7]/g, '')
        .replace(/[_\-./()\\\[\]{}:;,#@!?]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

/**
 * Split a normalized name into tokens, de-pluralizing a common trailing 's' so
 * "volumes" matches "volume" and "contracts" matches "contract".
 */
export function tokenizeColumnName(normalized: string): string[] {
    return normalized.split(' ')
        .filter(w => w.length > 0)
        .map(w => (w.length > 3 && w.endsWith('s') && !w.endsWith('ss')) ? w.slice(0, -1) : w);
}

/** Score how well a file column matches one alias on a [0, 1] scale. 0 = no match. */
export function scoreColumnAlias(columnName: string, alias: string): number {
    const colNorm = normalizeColumnName(columnName);
    const aliNorm = normalizeColumnName(alias);
    if (!colNorm || !aliNorm) return 0;

    // 1.0 - exact normalized match (handles case, separators, asterisks).
    if (colNorm === aliNorm) return 1.0;

    const colTokens = tokenizeColumnName(colNorm);
    const aliTokens = tokenizeColumnName(aliNorm);
    if (colTokens.length === 0 || aliTokens.length === 0) return 0;

    const colSet = new Set(colTokens);
    const aliSet = new Set(aliTokens);

    // 0.95 - same set of tokens (different order and/or plural form).
    if (colSet.size === aliSet.size && aliTokens.every(t => colSet.has(t))) {
        return 0.95;
    }

    // 0.85 - every alias token is contained in the column (alias is a subset).
    // Restricted to multi-token aliases so short aliases like "Date" or "Type"
    // don't wrongly grab columns such as "Settlement Date" or "Account Type".
    if (aliTokens.length >= 2 && aliTokens.every(t => colSet.has(t))) {
        return 0.85;
    }

    // 0.8 - every column token is contained in the alias (column is a subset).
    // Same multi-token guard.
    if (colTokens.length >= 2 && colTokens.every(t => aliSet.has(t))) {
        return 0.8;
    }

    return 0;
}

/**
 * Best-scoring field for a column, or null when nothing clears MIN_COLUMN_MATCH_SCORE.
 * Unlike the original this returns the score too, so the mapping UI can show the user
 * how confident the match was instead of silently applying it.
 */
export function matchColumnToField(
    columnName: string,
    fields: readonly ColumnMatchable[]
): ColumnMatch | null {
    let best: ColumnMatch | null = null;

    for (const field of fields) {
        for (const alias of field.aliases) {
            const score = scoreColumnAlias(columnName, alias);
            if (score > (best?.score ?? 0)) {
                best = { fieldKey: field.key, score };
                // Can't beat an exact match - stop looking.
                if (score >= 1) return best;
            }
        }
    }

    return best && best.score >= MIN_COLUMN_MATCH_SCORE ? best : null;
}

/**
 * Match every column at once. Returns an entry for EVERY column, with a null match
 * where nothing scored high enough - the mapping UI needs to show unmapped columns
 * too, not just the ones that resolved.
 */
export function matchColumns(
    columnNames: readonly string[],
    fields: readonly ColumnMatchable[]
): { columnName: string; match: ColumnMatch | null }[] {
    return columnNames.map(columnName => ({
        columnName,
        match: matchColumnToField(columnName, fields),
    }));
}
